import React from 'react'
import PropTypes from 'prop-types'
import { IntlProvider } from 'react-intl'
import { EPProvider } from '@elparking/components'
import convertConstantToEPProviderFormat from 'commons/js/util/coverterGlobalConfig'

import ParkingRateForm from 'parking-availability/js/components/ParkingRateForm'
import theme from 'parking-availability/js/theme'

import { compactObject } from 'commons/js/util/objectUtils'
import { maybeParseTimestamp, formatDateTime } from 'commons/js/util/dateConverter'

import { toUrlSearchParams } from 'commons/js/util/UrlTo'
import { messages } from 'commons/js/messages'
import { defaultLocale, currentLocale } from 'commons/js/locale'

import { trackParkingDetailChangeDateEvent, trackParkingDetailSearchEvent } from 'commons/js/services/analytics'
import { navigateToBookingProcess } from 'parking-availability/js/services/bookingService'
import { createPrebooking } from 'parking-availability/js/services/prebookingService'
import isAvailabilityEqual from 'parking-availability/js/containers/isAvailabilityEqual'
import findBestAvailableProduct from 'parking-availability/js/util/findBestAvailableProduct'
import UrlParams from 'commons/js/util/UrlTo/UrlParams'
import { createPublicPrebooking, getBookingProductsAvailability, getUser } from 'commons/js/api'

const EMPTY_USER_ERROR = 'EMPTY_ERROR'
function getId (bookingProduct) {
    return bookingProduct && bookingProduct.product && bookingProduct.product.id
}

function getSearch ({ parkingId, startDate, endDate }) {
    return compactObject({
        parkingId,
        startDate,
        endDate,
    })
}

function getPrebookingParams (state) {
    return {
        parkingId: state.parkingId,
        bookingProductId: getId(state.selectedBookingProduct),
        startDate: state.startDate,
        endDate: state.endDate,
    }
}

function mapSearchToParams (search) {
    const startDate = maybeParseTimestamp(search.startDate)
    const endDate = maybeParseTimestamp(search.endDate)

    return compactObject({
        start_date: startDate && formatDateTime(startDate),
        end_date: endDate && formatDateTime(endDate),
    })
}

class BookingAvailabilityContainer extends React.PureComponent {
    static propTypes = {
        parkingId: PropTypes.string,
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        location: PropTypes.object,
        push: PropTypes.func,
        getBookingAvailability: PropTypes.func,
        createPrebooking: PropTypes.func,
        navigateToBookingProcess: PropTypes.func,
        createPublicPrebooking: PropTypes.func,
        getUser: PropTypes.func,
    }

    static defaultProps = {
        getBookingAvailability: getBookingProductsAvailability,
        createPrebooking,
        navigateToBookingProcess,
        createPublicPrebooking,
        getUser,
    }

    state = {
        parkingId: this.props.parkingId,
        startDate: this.props.startDate,
        endDate: this.props.endDate,
        pending: false,
        errors: [],
        selectedBookingProduct: null,
        bookingProducts: [],
        userId: null,
        getUserError: null,
    }

    receiveAvailability = this.receiveAvailability.bind(this)
    receivePrebooking = this.receivePrebooking.bind(this)
    receiveErrors = this.receiveErrors.bind(this)
    receiveUser = this.receiveUser.bind(this)
    findAvailability = this.findAvailability.bind(this)
    requestUser = this.requestUser.bind(this)
    updateUrl = this.updateUrl.bind(this)
    handleChange = this.handleChange.bind(this)
    handleSubmit = this.handleSubmit.bind(this)

    componentDidMount () {
        this.updateUrl(getSearch(this.state))
        this.findAvailability(getSearch(this.state))
        this.requestUser()
    }

    componentDidUpdate (_, prevState) {
        const shouldUpdateAvailability = !isAvailabilityEqual(
            getSearch(this.state),
            getSearch(prevState)
        )

        if (shouldUpdateAvailability) {
            this.updateUrl(getSearch(this.state))
            this.findAvailability(getSearch(this.state))
            trackParkingDetailChangeDateEvent(getSearch(this.state))
        }
    }

    requestUser () {
        this.setState(() => ({ pending: true }))
        this.props.getUser()
            .then(
                this.receiveUser,
            )
            .catch(this.receiveErrors)
    }

    receiveUser (response) {
        if (!response) {
            return
        }

        if (response.error && response.error.name !== EMPTY_USER_ERROR) {
            this.setState(() => ({
                pending: false,
                getUserError: response.error,
                userId: null,
            }))
            return
        }
        this.setState(() => ({
            pending: false,
            userId: response?.result?.id,
            getUserError: null,
        }))
    }

    findAvailability (availabilityParams) {
        const { startDate, endDate } = availabilityParams

        if (!startDate || !endDate) {
            return
        }

        this.setState(() => ({ pending: true }))
        this.props.getBookingAvailability(availabilityParams)
            .then(
                this.receiveAvailability,
            )
            .catch(this.receiveErrors)
    }

    updateUrl (params) {
        const locationParams = new UrlParams()
        locationParams.loadFromUrl()
        const availabilityParams = new UrlParams()
        availabilityParams.mergeWithUrlParams(toUrlSearchParams(mapSearchToParams(params)))
        const shouldUpdateUrl = locationParams.toString(true) !== availabilityParams.toString(true)

        if (shouldUpdateUrl) {
            this.props.push({ search: availabilityParams.toString(true) })
        }
    }

    receiveAvailability (availability) {
        if (availability.canceled) {
            this.setState(() => ({
                pending: false,
            }))
            return
        }

        if (availability.error) {
            this.setState(() => ({
                pending: false,
                errors: availability.error,
                selectedBookingProduct: null,
                bookingProducts: [],
            }))
            return
        }

        this.setState(() => ({
            pending: false,
            errors: [],
            selectedBookingProduct: findBestAvailableProduct(availability.result),
            bookingProducts: availability.result,
        }))
    }

    receivePrebooking (prebooking) {
        if (prebooking.error) {
            this.setState(() => ({
                pending: false,
                errors: prebooking.error,
            }))
            return
        }

        this.props.navigateToBookingProcess(prebooking.result)
        trackParkingDetailSearchEvent(getSearch(this.state))
    }

    receiveErrors () {
        this.setState(() => ({ pending: false }))
    }

    handleChange (props) {
        this.setState(props)
    }

    handleSubmit (selectedBookingProduct) {
        this.setState(
            () => ({
                pending: true,
                selectedBookingProduct,
            }),
            () => {
                this.state.userId ? this.props.createPrebooking(getPrebookingParams(this.state))
                    .then(
                        this.receivePrebooking,

                    ).catch(this.receiveErrors) : this.props.createPublicPrebooking(getPrebookingParams(this.state))
                    .then(
                        this.receivePrebooking,
                    ).catch(this.receiveErrors)
            }
        )
    }

    render () {
        const { startDate, endDate, pending, errors, bookingProducts, selectedBookingProduct, getUserError } = this.state

        return (
            <IntlProvider locale={currentLocale} defaultLocale={defaultLocale} messages={messages[currentLocale]}>
                <EPProvider theme={theme} {...convertConstantToEPProviderFormat()}>
                    <ParkingRateForm
                      startDate={startDate}
                      endDate={endDate}
                      selectedBookingProduct={selectedBookingProduct}
                      bookingProducts={bookingProducts}
                      errors={errors}
                      getUserError={getUserError}
                      isLoading={pending}
                      onChange={this.handleChange}
                      onSubmit={this.handleSubmit}
                    />
                </EPProvider>
            </IntlProvider>
        )
    }
}

export default BookingAvailabilityContainer
